'use client';
import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface FormState {
  fullName: string;
  email: string;
  city: string;
  affiliation: string;
  whyJoin: string;
}

const INITIAL: FormState = {
  fullName: '',
  email: '',
  city: '',
  affiliation: '',
  whyJoin: '',
};

const MemberSignupSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.city.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const supabase = createClient();
      const { error } = await supabase.from('member_signups').insert({
        full_name: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        city: form.city.trim(),
        affiliation: form.affiliation.trim() || null,
        why_join: form.whyJoin.trim() || null,
      });

      if (error) {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
        return;
      }

      setStatus('success');
      setForm(INITIAL);
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <section
      id="join"
      ref={sectionRef}
      className="py-20 md:py-28 px-6"
      style={{ background: 'var(--forest)' }}
    >
      <style>{`
        .signup-field {
          position: relative;
        }
        .signup-input {
          width: 100%;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 10px;
          padding: 14px 16px;
          font-family: 'Fraunces', serif;
          font-size: 15px;
          color: rgba(255,255,255,0.92);
          outline: none;
          transition: border-color 0.25s ease, background 0.25s ease;
          -webkit-appearance: none;
        }
        .signup-input::placeholder {
          color: rgba(255,255,255,0.3);
        }
        .signup-input:focus {
          border-color: rgba(135,146,107,0.7);
          background: rgba(255,255,255,0.09);
        }
        .signup-label {
          display: block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.45);
          margin-bottom: 8px;
        }
        .signup-label .req {
          color: rgba(181,88,46,0.8);
          margin-left: 2px;
        }
        .signup-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: var(--sage);
          color: var(--forest);
          font-family: 'Fraunces', serif;
          font-size: 15px;
          font-weight: 600;
          padding: 14px 32px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
          letter-spacing: 0.01em;
        }
        .signup-btn:hover:not(:disabled) {
          background: #9aa87c;
          transform: translateY(-1px);
        }
        .signup-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .signup-card {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.65s cubic-bezier(0.22,1,0.36,1), transform 0.65s cubic-bezier(0.22,1,0.36,1);
        }
        .signup-card.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .signup-card.delay-1 { transition-delay: 0.1s; }
        .signup-card.delay-2 { transition-delay: 0.22s; }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`signup-card${visible ? ' visible' : ''}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6"
            style={{ borderColor: 'rgba(135,146,107,0.3)', background: 'rgba(135,146,107,0.08)' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--sage)' }} />
            <span className="font-mono font-bold uppercase" style={{ fontSize: '10px', letterSpacing: '0.2em', color: 'var(--sage)' }}>
              Become a Member
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-14 items-start">
            <div>
              <h2 className="section-headline-light">
                Join the{' '}
                <em style={{ fontStyle: 'italic', color: 'var(--sage)' }}>
                  network.
                </em>
              </h2>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '16px', lineHeight: 1.7, fontWeight: 300, maxWidth: '420px' }} className="md:pt-2">
              NTBI is building a community of people who care about urban biodiversity. Sign up to be counted — and to stay connected as our work grows.
            </p>
          </div>
        </div>

        {/* Form card */}
        <div className={`signup-card delay-1${visible ? ' visible' : ''}`}>
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: 'clamp(28px, 5vw, 52px)',
          }}>
            {status === 'success' ? (
              <div className="flex flex-col items-start gap-4 py-8">
                <div style={{
                  width: '52px', height: '52px', borderRadius: '50%',
                  background: 'rgba(135,146,107,0.15)',
                  border: '1px solid rgba(135,146,107,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M4 11.5L9 16.5L18 6" stroke="var(--sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <p style={{ fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 600, color: 'rgba(255,255,255,0.92)', marginBottom: '8px' }}>
                    You&apos;re in.
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '15px', lineHeight: 1.6 }}>
                    Thanks for signing up. We&apos;ll be in touch as NTBI grows.
                  </p>
                </div>
                <button
                  onClick={() => setStatus('idle')}
                  className="signup-btn"
                  style={{ marginTop: '8px', background: 'rgba(135,146,107,0.15)', color: 'var(--sage)', border: '1px solid rgba(135,146,107,0.3)' }}
                >
                  Sign up another person
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                  {/* Full name */}
                  <div className="signup-field">
                    <label className="signup-label" htmlFor="fullName">
                      Full name<span className="req">*</span>
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      className="signup-input"
                      placeholder="Your full name"
                      value={form.fullName}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                    />
                  </div>

                  {/* Email */}
                  <div className="signup-field">
                    <label className="signup-label" htmlFor="email">
                      Email<span className="req">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="signup-input"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>

                  {/* City */}
                  <div className="signup-field">
                    <label className="signup-label" htmlFor="city">
                      City / Location<span className="req">*</span>
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      className="signup-input"
                      placeholder="Dallas, TX"
                      value={form.city}
                      onChange={handleChange}
                      required
                      autoComplete="address-level2"
                    />
                  </div>

                  {/* Affiliation */}
                  <div className="signup-field">
                    <label className="signup-label" htmlFor="affiliation">
                      School or affiliation
                    </label>
                    <input
                      id="affiliation"
                      name="affiliation"
                      type="text"
                      className="signup-input"
                      placeholder="Optional"
                      value={form.affiliation}
                      onChange={handleChange}
                      autoComplete="organization"
                    />
                  </div>

                  {/* Why join — full width */}
                  <div className="signup-field md:col-span-2">
                    <label className="signup-label" htmlFor="whyJoin">
                      Why do you want to join?
                    </label>
                    <textarea
                      id="whyJoin"
                      name="whyJoin"
                      className="signup-input"
                      placeholder="Optional — a sentence or two is plenty."
                      value={form.whyJoin}
                      onChange={handleChange}
                      rows={3}
                      style={{ resize: 'vertical', minHeight: '88px' }}
                    />
                  </div>
                </div>

                {/* Error */}
                {status === 'error' && (
                  <p style={{ color: 'rgba(181,88,46,0.9)', fontSize: '13px', marginTop: '16px', fontFamily: "'JetBrains Mono', monospace" }}>
                    {errorMsg}
                  </p>
                )}

                {/* Submit row */}
                <div className="flex items-center gap-6 mt-8 flex-wrap">
                  <button
                    type="submit"
                    className="signup-btn"
                    disabled={status === 'loading' || !form.fullName || !form.email || !form.city}
                  >
                    {status === 'loading' ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" />
                        </svg>
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                  <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>
                    Fields marked <span style={{ color: 'rgba(181,88,46,0.7)' }}>*</span> are required
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Bottom note */}
        <div className={`signup-card delay-2${visible ? ' visible' : ''}`}>
          <p className="mt-8 font-mono" style={{ fontSize: '11px', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)' }}>
            Your information is stored securely and will only be used to keep you informed about NTBI&apos;s work.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
};

export default MemberSignupSection;
